// client/src/hooks/useModalState.js
import { useState, useCallback, useMemo } from 'react'

/**
 * Custom hook to manage modal state and loading states across AdminLeagueView
 *
 * Centralizes all modal visibility state and associated loading flags,
 * providing a clean interface for opening/closing modals and managing async operations.
 */
export default function useModalState() {
  // Modal visibility state
  const [showDeleteModal, setShowDeleteModal] = useState(null) // null or league object
  const [showPlayoffModal, setShowPlayoffModal] = useState(null) // null or league object
  const [showRemovePlayerModal, setShowRemovePlayerModal] = useState(null) // null or player object

  // Playoff modal additional state
  const [playoffEligibilityInfo, setPlayoffEligibilityInfo] = useState(null)

  // Loading states
  const [isStartingPlayoffs, setIsStartingPlayoffs] = useState(false)
  const [isPublishingBracket, setIsPublishingBracket] = useState(false)
  const [isLoadingBracket, setIsLoadingBracket] = useState(false)
  const [isRemovingPlayer, setIsRemovingPlayer] = useState(false)
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)

  // Delete modal actions
  const openDeleteModal = useCallback((league) => {
    setShowDeleteModal(league)
  }, [])

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(null)
  }, [])

  // Playoff modal actions
  const openPlayoffModal = useCallback((league, eligibilityInfo) => {
    setShowPlayoffModal(league)
    setPlayoffEligibilityInfo(eligibilityInfo)
  }, [])

  const closePlayoffModal = useCallback(() => {
    setShowPlayoffModal(null)
    setPlayoffEligibilityInfo(null)
  }, [])

  // Remove player modal actions
  const openRemovePlayerModal = useCallback((player) => {
    setShowRemovePlayerModal(player)
  }, [])

  const closeRemovePlayerModal = useCallback(() => {
    setShowRemovePlayerModal(null)
  }, [])

  // Loading state setters map
  const loadingStateSetters = useMemo(
    () => ({
      startingPlayoffs: setIsStartingPlayoffs,
      publishingBracket: setIsPublishingBracket,
      loadingBracket: setIsLoadingBracket,
      removingPlayer: setIsRemovingPlayer,
      downloadingCsv: setIsDownloadingCsv,
    }),
    [],
  )

  // Generic loading state setter
  const setLoadingState = useCallback(
    (stateName, value) => {
      const setter = loadingStateSetters[stateName]
      if (setter) {
        setter(value)
      } else {
        console.warn(`Unknown loading state: ${stateName}`)
      }
    },
    [loadingStateSetters],
  )

  return {
    // Delete modal state and actions
    deleteModal: {
      show: showDeleteModal !== null,
      league: showDeleteModal,
    },
    openDeleteModal,
    closeDeleteModal,

    // Playoff modal state and actions
    playoffModal: {
      show: showPlayoffModal !== null,
      league: showPlayoffModal,
      eligibilityInfo: playoffEligibilityInfo,
    },
    openPlayoffModal,
    closePlayoffModal,

    // Remove player modal state and actions
    removePlayerModal: {
      show: showRemovePlayerModal !== null,
      player: showRemovePlayerModal,
    },
    openRemovePlayerModal,
    closeRemovePlayerModal,

    // Loading states
    loadingStates: {
      isStartingPlayoffs,
      isPublishingBracket,
      isLoadingBracket,
      isRemovingPlayer,
      isDownloadingCsv,
    },
    setLoadingState,
  }
}
